import React, { FunctionComponent, ReactNode, useCallback, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import OrganizationsTable from './organizations-table';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { AdminApi, AdminOrganization, AdminOrganizationsListOrderingEnum } from 'src/api';
import DeleteOrganizationConfirmationModal from './delete-organization-confirmation-modal';
import DeleteOrganizationSuccessModal from './delete-organization-success-modal';
import Button from 'src/ui/atoms/button';
import useGoTo from 'src/hooks/use-go-to';
import { ISSUER_ROUTES } from '../routes.config';
import AddOrganization from './add-organization';
import Spacer from 'src/ui/atoms/spacer';
import { hasViewOrganizationDataPermissionOnAtLeastOneOrganization } from 'src/apps/issuer/helpers/organizationPermissions';
import { currentUserHasManageOrganizationsPermission } from 'src/apps/issuer/helpers/userPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import PageWrapper from 'src/core/layout/content/page-wrapper';
interface IssuerOrganizationsTableProps {
  status?: string;
  title?: ReactNode;
  onDelete: (organization: AdminOrganization) => void;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const IssuerOrganizationsTable: FunctionComponent<IssuerOrganizationsTableProps> = WithDataRecord<
  AdminApi,
  AdminOrganization
>(AdminApi)(
  ({ data, ordering, onOrderBy, loading, hasPermission, paginationProps, onDelete, setFilterPopupHeight }) => {
    const goTo = useGoTo(ISSUER_ROUTES.organizationDetails);

    return (
      <>
        <OrganizationsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          organizations={data}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          onDelete={onDelete}
          goToDetails={(organizationId: string) => goTo({ organizationId })}
          setFilterPopupHeight={setFilterPopupHeight}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminOrganizationsList({
      offset,
      limit,
      ordering: ordering as AdminOrganizationsListOrderingEnum,
      ...props,
    });
  },
);

const IssuerOrganizationsPage = () => {
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);
  const [isAddOrganizationModalOpen, setAddOrganizationModalOpen] = useState(false);
  const [deleteOrganizationModalData, setDeleteOrganizationModalData] = useState<AdminOrganization | null>(null);
  const [isdeleteOrganizationSuccessOpen, setDeleteOrganizationSuccessOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);

  const { currentUser } = useCurrentUserSelector();
  const hasPermission =
    hasViewOrganizationDataPermissionOnAtLeastOneOrganization(currentUser) ||
    currentUserHasManageOrganizationsPermission(currentUser?.permissions || []);

  const onDeleteOrganizationSuccess = useCallback(() => {
    setDeleteOrganizationSuccessOpen(true);
    refreshTableData();
  }, []);

  const onCloseDeleteOrganizationModal = useCallback(() => {
    setDeleteOrganizationModalData(null);
    setDeleteOrganizationSuccessOpen(false);
  }, []);

  const onAddOrganizationSuccess = useCallback(() => {
    setAddOrganizationModalOpen(false);
    refreshTableData();
  }, []);

  const refreshTableData = useCallback(() => {
    setForceDataLoad(true);
  }, []);

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboard.issuerOrganizations" />} titleSpacing="larger">
        <Button
          variant="primary"
          icon="plus"
          size="small"
          iconSize="small"
          iconColor="inherit"
          onClick={() => setAddOrganizationModalOpen(true)}
        >
          <Translate name="addOrganization.title" />
        </Button>
        <Spacer y={4} />
        <IssuerOrganizationsTable
          forceDataLoad={forceDataLoad}
          setForceDataLoad={setForceDataLoad}
          hasPermission={hasPermission}
          onDelete={(organization: AdminOrganization) => setDeleteOrganizationModalData(organization)}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {isAddOrganizationModalOpen && (
          <AddOrganization
            onClose={() => setAddOrganizationModalOpen(false)}
            onAddNewOrganization={onAddOrganizationSuccess}
          />
        )}
        {!!deleteOrganizationModalData && (
          <DeleteOrganizationConfirmationModal
            onDeleteOrganization={onDeleteOrganizationSuccess}
            onClose={() => setDeleteOrganizationModalData(null)}
            data={deleteOrganizationModalData}
          />
        )}
        {!!isdeleteOrganizationSuccessOpen && (
          <DeleteOrganizationSuccessModal onClose={onCloseDeleteOrganizationModal} />
        )}
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerOrganizationsPage, AppType.ISSUER);
